import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus, DownloadSimple, UploadSimple, MagnifyingGlass, LinkedinLogo, Trash, ArrowsClockwise, MagicWand, Spinner, PaperPlaneTilt } from "@phosphor-icons/react";
import api, { formatApiErrorDetail } from "@/lib/api";
import AppShell from "@/components/AppShell";
import { PageHeader } from "@/components/PageHeader";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { ScoreBadge } from "@/components/ScoreBadge";
import LeadDrawer from "@/components/LeadDrawer";
import ImportSalesNavigator from "@/components/ImportSalesNavigator";
import ConfirmDialog from "@/components/ConfirmDialog";
import { toast } from "sonner";

function toCsv(rows) {
  if (!rows.length) return "";
  const headers = [
    "business_name", "industry", "city", "state", "website", "owner_name",
    "email", "phone", "linkedin_url", "google_business_url",
    "website_quality_score", "seo_score", "review_score", "lead_score",
    "pain_points", "recommended_offer", "status", "next_follow_up_date", "notes",
  ];
  const esc = (v) => {
    const s = String(v ?? "");
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  return [headers.join(","), ...rows.map((r) => headers.map((h) => esc(r[h])).join(","))].join("\n");
}

function parseCsv(text) {
  const lines = text.split(/\r?\n/).filter((l) => l.trim());
  if (lines.length < 2) return [];
  const parseLine = (line) => {
    const out = []; let cur = ""; let q = false;
    for (let i = 0; i < line.length; i++) {
      const c = line[i];
      if (q) {
        if (c === '"' && line[i + 1] === '"') { cur += '"'; i++; }
        else if (c === '"') { q = false; }
        else { cur += c; }
      } else {
        if (c === ",") { out.push(cur); cur = ""; }
        else if (c === '"') { q = true; }
        else { cur += c; }
      }
    }
    out.push(cur);
    return out;
  };
  const headers = parseLine(lines[0]).map((h) => h.trim());
  return lines.slice(1).map((line) => {
    const cells = parseLine(line);
    const obj = {};
    headers.forEach((h, i) => { obj[h] = cells[i] ?? ""; });
    ["website_quality_score", "seo_score", "review_score", "lead_score"].forEach((k) => {
      if (obj[k]) obj[k] = Number(obj[k]) || 0;
    });
    return obj;
  });
}

export default function Leads() {
  const [leads, setLeads] = useState([]);
  const [stages, setStages] = useState([]);
  const [q, setQ] = useState("");
  const [stageFilter, setStageFilter] = useState("All");
  const [qualityFilter, setQualityFilter] = useState("All");
  const [openLeadId, setOpenLeadId] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [salesNavOpen, setSalesNavOpen] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [bulkStage, setBulkStage] = useState("");
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkEnriching, setBulkEnriching] = useState(false);
  const [bulkOutreaching, setBulkOutreaching] = useState(false);

  const load = async () => {
    const [m, l] = await Promise.all([api.get("/meta"), api.get("/leads")]);
    setStages(m.data.stages);
    setLeads(l.data);
  };
  useEffect(() => { load(); }, []);

  // Restore drawer from ?lead=<id> deep-link on mount + URL changes
  useEffect(() => {
    const id = searchParams.get("lead");
    if (id && id !== openLeadId) setOpenLeadId(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const filtered = useMemo(() => {
    const lq = q.toLowerCase();
    return leads.filter((l) => {
      if (stageFilter !== "All" && l.status !== stageFilter) return false;
      if (qualityFilter === "ReadyToClose") {
        if ((l.lead_score || 0) < 70) return false;
        if (!l.phone && !l.email) return false;
      } else if (qualityFilter === "Hot") {
        if ((l.lead_score || 0) < 80) return false;
      } else if (qualityFilter === "Auditable") {
        if (!l.website) return false;
      } else if (qualityFilter === "CoolingOff") {
        const stamp = l.updated_at || l.created_at;
        if (!stamp) return false;
        const days = (Date.now() - new Date(stamp).getTime()) / 86400000;
        if (days < 30) return false;
        // Cooling off = pipeline is warm (not Won/Lost) but neglected
        if (l.status === "Won" || l.status === "Lost") return false;
      }
      if (!lq) return true;
      return (
        (l.business_name || "").toLowerCase().includes(lq) ||
        (l.industry || "").toLowerCase().includes(lq) ||
        (l.city || "").toLowerCase().includes(lq) ||
        (l.owner_name || "").toLowerCase().includes(lq) ||
        (l.email || "").toLowerCase().includes(lq)
      );
    });
  }, [leads, q, stageFilter, qualityFilter]);

  const exportCsv = () => {
    const csv = toCsv(filtered);
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `clickforge-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    toast.success(`Exported ${filtered.length} leads`);
  };

  const importCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    const rows = parseCsv(text);
    let ok = 0; let fail = 0;
    for (const row of rows) {
      try {
        if (!row.business_name) continue;
        const { data } = await api.post("/leads", row);
        setLeads((prev) => [data, ...prev]);
        ok++;
      } catch {
        fail++;
      }
    }
    toast.success(`Imported ${ok}${fail ? `, ${fail} failed` : ""}`);
    e.target.value = "";
  };

  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    if (selected.size === filtered.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filtered.map((l) => l.id)));
    }
  };

  const bulkUpdateStatus = async () => {
    if (!bulkStage) return;
    const ids = Array.from(selected);
    try {
      const { data } = await api.post("/leads/bulk/status", { lead_ids: ids, status: bulkStage });
      toast.success(`Moved ${data.updated} leads → ${bulkStage}`);
      setLeads((p) => p.map((l) => (selected.has(l.id) ? { ...l, status: bulkStage } : l)));
      setSelected(new Set());
      setBulkStage("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Bulk update failed");
    }
  };

  const bulkDeleteRun = async () => {
    setBulkDeleteOpen(false);
    const ids = Array.from(selected);
    try {
      const { data } = await api.post("/leads/bulk/delete", { lead_ids: ids });
      toast.success(`Deleted ${data.deleted} leads${data.deleted_transactions ? ` + ${data.deleted_transactions} proposals` : ""}`);
      setLeads((p) => p.filter((l) => !selected.has(l.id)));
      setSelected(new Set());
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Bulk delete failed");
    }
  };

  const bulkEnrich = async () => {
    const ids = Array.from(selected).slice(0, 25);
    if (ids.length === 0) return;
    setBulkEnriching(true);
    try {
      const { data } = await api.post("/hunter/enrich-bulk", { lead_ids: ids });
      if (!data.configured) {
        toast.error("Hunter.io API key not configured. Add HUNTER_API_KEY in Settings.");
        return;
      }
      const summary = `Enriched ${data.updated} · matched ${data.matched} · ${data.processed} processed${data.quota_hit ? " · quota hit" : ""}`;
      if (data.updated > 0) toast.success(summary);
      else toast.info(summary);
      await load();
      setSelected(new Set());
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Bulk enrich failed");
    } finally {
      setBulkEnriching(false);
    }
  };

  const bulkOutreach = async () => {
    const ids = Array.from(selected).slice(0, 20);
    if (ids.length === 0) return;
    if (!window.confirm(`Send AI outreach emails to ${ids.length} selected lead${ids.length === 1 ? "" : "s"}? This will take about ${ids.length * 2} seconds.`)) {
      return;
    }
    setBulkOutreaching(true);
    const toastId = toast.loading(`Sending to ${ids.length} leads… ~${ids.length * 2}s`);
    try {
      const { data } = await api.post("/email/send-outreach-bulk", { lead_ids: ids });
      toast.dismiss(toastId);
      if (data.sent > 0) {
        toast.success(data.message, { duration: 8000 });
      } else {
        toast.warning(data.message, { duration: 8000 });
      }
      await load();
      setSelected(new Set());
    } catch (err) {
      toast.dismiss(toastId);
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Bulk outreach failed");
    } finally {
      setBulkOutreaching(false);
    }
  };

  return (
    <AppShell>
      <PageHeader
        eyebrow="All Leads"
        title="Spreadsheet view"
        subtitle="Full lead database — search, filter, import, export."
        testid="leads-header"
      >
        <Button
          data-testid="leads-add-btn"
          onClick={() => setOpenLeadId("__new__")}
          className="bg-orange-600 hover:bg-orange-500 text-zinc-950 rounded-sm"
        >
          <Plus size={16} className="mr-2" /> Add lead
        </Button>
        <Button variant="outline" data-testid="leads-export-btn" onClick={exportCsv} className="border-zinc-800 hover:bg-zinc-900 rounded-sm">
          <DownloadSimple size={16} className="mr-2" /> Export
        </Button>
        <label className="inline-flex">
          <input type="file" accept=".csv" data-testid="leads-import-input" onChange={importCsv} className="hidden" />
          <span className="inline-flex items-center gap-2 px-4 py-2 text-sm border border-zinc-800 hover:bg-zinc-900 text-zinc-200 rounded-sm cursor-pointer">
            <UploadSimple size={16} /> Import CSV
          </span>
        </label>
        <Button
          variant="outline"
          data-testid="leads-salesnav-btn"
          onClick={() => setSalesNavOpen(true)}
          className="border-orange-600/40 hover:bg-orange-600/10 text-orange-300 hover:text-orange-200 rounded-sm"
        >
          <LinkedinLogo size={16} className="mr-2" weight="duotone" /> Sales Navigator
        </Button>
      </PageHeader>

      <div className="px-4 md:px-10 py-6 space-y-4">
        {selected.size > 0 && (
          <div
            data-testid="bulk-action-bar"
            className="bg-orange-600/10 border border-orange-600/40 rounded-sm p-3 flex flex-wrap items-center gap-3"
          >
            <div className="font-mono text-xs text-orange-300" data-testid="bulk-selected-count">
              {selected.size} selected
            </div>
            <div className="flex-1" />
            <Select value={bulkStage} onValueChange={setBulkStage}>
              <SelectTrigger data-testid="bulk-stage-select" className="w-[220px] bg-zinc-950 border-zinc-800 text-zinc-100 text-xs">
                <SelectValue placeholder="Move to stage…" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-950 border-zinc-800">
                {stages.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button
              size="sm"
              data-testid="bulk-apply-stage-btn"
              disabled={!bulkStage}
              onClick={bulkUpdateStatus}
              className="bg-orange-600 hover:bg-orange-500 text-zinc-950 rounded-sm"
            >
              <ArrowsClockwise size={14} className="mr-1.5" /> Apply
            </Button>
            <Button
              size="sm"
              data-testid="bulk-enrich-hunter-btn"
              disabled={bulkEnriching || selected.size === 0}
              onClick={bulkEnrich}
              className="bg-emerald-600 hover:bg-emerald-500 text-zinc-950 rounded-sm"
              title="Enrich owner emails via Hunter.io (up to 25 at a time)"
            >
              {bulkEnriching ? (<><Spinner size={14} className="mr-1.5 animate-spin" /> Enriching…</>) : (<><MagicWand size={14} className="mr-1.5" /> Enrich all ({Math.min(selected.size, 25)})</>)}
            </Button>
            <Button
              size="sm"
              data-testid="bulk-outreach-btn"
              disabled={bulkOutreaching || selected.size === 0}
              onClick={bulkOutreach}
              className="bg-orange-600 hover:bg-orange-500 text-zinc-950 rounded-sm"
              title="Send AI outreach to up to 20 selected leads (2s between sends)"
            >
              {bulkOutreaching ? (<><Spinner size={14} className="mr-1.5 animate-spin" /> Sending…</>) : (<><PaperPlaneTilt size={14} className="mr-1.5" weight="fill" /> Send outreach ({Math.min(selected.size, 20)})</>)}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              data-testid="bulk-delete-btn"
              onClick={() => setBulkDeleteOpen(true)}
              className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
            >
              <Trash size={14} className="mr-1.5" /> Delete
            </Button>
            <Button
              size="sm"
              variant="ghost"
              data-testid="bulk-clear-btn"
              onClick={() => setSelected(new Set())}
              className="text-zinc-400 hover:text-zinc-100"
            >
              Clear
            </Button>
          </div>
        )}

        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative max-w-sm flex-1">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <Input
              data-testid="leads-search-input"
              placeholder="Search business, industry, city, owner…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-9 bg-zinc-900 border-zinc-800"
            />
          </div>
          <Select value={stageFilter} onValueChange={setStageFilter}>
            <SelectTrigger data-testid="leads-stage-filter" className="w-[200px] bg-zinc-900 border-zinc-800"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-zinc-950 border-zinc-800">
              <SelectItem value="All">All stages</SelectItem>
              {stages.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={qualityFilter} onValueChange={setQualityFilter}>
            <SelectTrigger data-testid="leads-quality-filter" className="w-[180px] bg-zinc-900 border-zinc-800"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-zinc-950 border-zinc-800">
              <SelectItem value="All">All quality</SelectItem>
              <SelectItem value="ReadyToClose">Ready to close (score ≥70 + contact)</SelectItem>
              <SelectItem value="Hot">Hot (score ≥80)</SelectItem>
              <SelectItem value="Auditable">Has website (auditable)</SelectItem>
              <SelectItem value="CoolingOff">Cooling off (30+ days, not closed)</SelectItem>
            </SelectContent>
          </Select>
          <div className="font-mono text-xs text-zinc-500 ml-auto" data-testid="leads-count">
            {filtered.length} of {leads.length}
          </div>
        </div>

        {/* Desktop table (md+) */}
        <div className="hidden md:block border border-zinc-800 rounded-sm overflow-x-auto bg-zinc-900/40">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-zinc-950 border-b border-zinc-800">
                <th className="px-3 py-3 w-8">
                  <Checkbox
                    data-testid="leads-select-all"
                    checked={filtered.length > 0 && selected.size === filtered.length}
                    onCheckedChange={toggleAll}
                  />
                </th>
                {["Business", "Industry", "City", "Owner", "Email", "Phone", "Stage", "Rating", "Score", ""].map((h) => (
                  <th key={h} className="text-left font-mono text-[10px] uppercase tracking-wider text-zinc-500 px-3 py-3">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={11} className="text-center text-zinc-500 py-10 text-sm" data-testid="leads-empty">No leads match these filters.</td></tr>
              )}
              {filtered.map((l) => (
                <tr
                  key={l.id}
                  data-testid={`leads-row-${l.id}`}
                  className={`border-b border-zinc-800/50 hover:bg-zinc-800/30 ${selected.has(l.id) ? "bg-orange-600/5" : ""}`}
                >
                  <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      data-testid={`leads-select-${l.id}`}
                      checked={selected.has(l.id)}
                      onCheckedChange={() => toggleOne(l.id)}
                    />
                  </td>
                  <td className="px-3 py-2.5 text-zinc-100 cursor-pointer" onClick={() => { setOpenLeadId(l.id); setSearchParams({ lead: l.id }); }}>{l.business_name}</td>
                  <td className="px-3 py-2.5 text-zinc-400 cursor-pointer" onClick={() => { setOpenLeadId(l.id); setSearchParams({ lead: l.id }); }}>{l.industry}</td>
                  <td className="px-3 py-2.5 text-zinc-400">{l.city}{l.state ? `, ${l.state}` : ""}</td>
                  <td className="px-3 py-2.5 text-zinc-400">{l.owner_name || "—"}</td>
                  <td className="px-3 py-2.5 text-zinc-400 font-mono text-xs truncate max-w-[180px]">{l.email || "—"}</td>
                  <td className="px-3 py-2.5 text-zinc-400 font-mono text-xs">{l.phone || "—"}</td>
                  <td className="px-3 py-2.5"><span className="stage-pill text-zinc-400">{l.status}</span></td>
                  <td className="px-3 py-2.5 text-xs font-mono">
                    {l.google_rating != null ? (
                      <span className="text-amber-400" title={`Google · ${l.google_review_count || 0} reviews`}>
                        {Number(l.google_rating).toFixed(1)}★ <span className="text-zinc-500">({l.google_review_count || 0})</span>
                      </span>
                    ) : l.yelp_rating != null ? (
                      <span className="text-red-400" title={`Yelp · ${l.yelp_review_count || 0} reviews`}>
                        {Number(l.yelp_rating).toFixed(1)}★ <span className="text-zinc-500">({l.yelp_review_count || 0})</span>
                      </span>
                    ) : (
                      <span className="text-zinc-600">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2.5"><ScoreBadge score={l.lead_score} size="sm" /></td>
                  <td className="px-3 py-2.5 text-zinc-600 text-xs cursor-pointer" onClick={() => { setOpenLeadId(l.id); setSearchParams({ lead: l.id }); }}>Edit ›</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile card list (< md) */}
        <div className="md:hidden space-y-2.5" data-testid="leads-mobile-cards">
          {filtered.length === 0 && (
            <div className="text-center text-zinc-500 py-10 text-sm border border-zinc-800 rounded-sm bg-zinc-900/40" data-testid="leads-empty-mobile">
              No leads match these filters.
            </div>
          )}
          {filtered.map((l) => (
            <div
              key={l.id}
              data-testid={`leads-card-${l.id}`}
              className={`border border-zinc-800 rounded-sm bg-zinc-900/40 p-3 ${selected.has(l.id) ? "border-orange-600/50 bg-orange-600/5" : ""}`}
            >
              <div className="flex items-start gap-3">
                <div className="pt-0.5" onClick={(e) => e.stopPropagation()}>
                  <Checkbox
                    data-testid={`leads-select-mobile-${l.id}`}
                    checked={selected.has(l.id)}
                    onCheckedChange={() => toggleOne(l.id)}
                  />
                </div>
                <div className="flex-1 min-w-0" onClick={() => { setOpenLeadId(l.id); setSearchParams({ lead: l.id }); }}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-zinc-100 font-medium truncate">{l.business_name}</div>
                      <div className="text-xs text-zinc-500 truncate mt-0.5">{l.industry}{l.city ? ` · ${l.city}${l.state ? `, ${l.state}` : ""}` : ""}</div>
                    </div>
                    <ScoreBadge score={l.lead_score} size="sm" />
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
                    <span className="stage-pill text-zinc-400">{l.status}</span>
                    {l.google_rating != null && (
                      <span className="text-amber-400 font-mono" title={`Google · ${l.google_review_count || 0} reviews`}>
                        {Number(l.google_rating).toFixed(1)}★ <span className="text-zinc-500">({l.google_review_count || 0})</span>
                      </span>
                    )}
                    {l.owner_name && <span className="text-zinc-400 truncate max-w-full">{l.owner_name}</span>}
                    {l.phone && <span className="text-zinc-500 font-mono">{l.phone}</span>}
                  </div>
                  {l.email && <div className="mt-1 text-xs text-zinc-400 font-mono truncate">{l.email}</div>}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <LeadDrawer
        leadId={openLeadId}
        stages={stages}
        onClose={() => { setOpenLeadId(null); setSearchParams({}); }}
        onUpdated={(updated) => setLeads((p) => p.map((x) => (x.id === updated.id ? updated : x)))}
        onCreated={(l) => setLeads((p) => [l, ...p])}
        onDeleted={(id) => setLeads((p) => p.filter((x) => x.id !== id))}
      />

      <ImportSalesNavigator
        open={salesNavOpen}
        onOpenChange={setSalesNavOpen}
        onImported={(newLeads) => setLeads((p) => [...newLeads, ...p])}
      />

      <ConfirmDialog
        open={bulkDeleteOpen}
        onOpenChange={setBulkDeleteOpen}
        title={`Delete ${selected.size} leads?`}
        description="This permanently deletes the selected leads and any pending Stripe proposals attached to them."
        confirmLabel={`Delete ${selected.size} leads`}
        destructive
        onConfirm={bulkDeleteRun}
        testid="confirm-bulk-delete"
      />
    </AppShell>
  );
}
